import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Panel } from 'primereact/panel'
import { FaChevronDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa'

import {
  Btn,
  CustomDropdown,
  CustomModal,
  PageHeader,
  Periodo,
  TextBtn,
} from '../../../../components'
import * as colors from '../../../../assets/js/colors'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import IconDriver from '../../../../assets/imgs/icon-driver.png'
import IconFinalFlag from '../../../../assets/imgs/icon-final_flag.png'
import IconInfo from '../../../../assets/imgs/icon-info.png'
import { getPermissions } from '../../../../services/localstorage/localstorage.service'
import { getLastReadMessageId } from '../../../../services/chat/chat-unread.service'
import { getRouteChatUnread } from '../../../../services/api/https/deliveries/delivery.http'
import usePainelMonitoramentoEntregas, { LOAD_MESSAGE } from './PainelMonitoramentoEntregasHook'
import ModalInformacoesEntrega from './ModalInformacoesEntrega'
import ModalInformacoesRota from './ModalInformacoesRota'
import ModalDelegarRotas from './ModalDelegarRotas'
import ModalAssumirRotas from './ModalAssumirRotas'
import './PainelMonitoramentoEntregas.css'

const PERMISSAO_DELEGAR_ROTAS = 146

const STATUS_COLORS = {
  8: colors.azul,
  9: colors.vinho,
  10: colors.azul_escuro,
  11: colors.gray,
  12: colors.laranja,
  13: colors.violeta,
  14: colors.verde_escuro,
  15: colors.roxo,
}

// Mesmo conjunto de STATUS_FINAIS da API (percentual do cabeçalho do TP)
const STATUS_FINAIS = [8, 9, 10, 12]

function PainelMonitoramentoEntregas({ dispatch, history, selectedTp }) {
  const { state, localDispatch } = usePainelMonitoramentoEntregas(selectedTp)
  const grupos = useMemo(() => (state.dados ? state.dados.grupos : []), [state.dados])
  const [collapsedByTp, setCollapsedByTp] = useState({})
  const hasPermissaoDelegarRotas = useMemo(
    () => getPermissions().includes(PERMISSAO_DELEGAR_ROTAS),
    [],
  )
  const [unreadChatFleets, setUnreadChatFleets] = useState([])

  // Indicador de mensagem nova por rota: reavaliado a cada refresh do painel
  // (grupos muda no auto-refresh de 60s) e ao fechar o modal da rota (chat lido).
  useEffect(() => {
    const fleetIds = grupos
      .reduce((ids, grupo) => ids.concat(grupo.rotas.map(rota => Number(rota.fleet_id))), [])
      .filter(Boolean)

    if (!fleetIds.length) {
      setUnreadChatFleets([])
      return undefined
    }

    let cancelled = false
    getRouteChatUnread(fleetIds)
      .then(data => {
        if (cancelled) return
        const items = data && Array.isArray(data.items) ? data.items : []
        setUnreadChatFleets(
          items
            .filter(item => Number(item.last_driver_message_id) > getLastReadMessageId(item.fleet_id))
            .map(item => Number(item.fleet_id)),
        )
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [grupos, state.modalRota])

  useEffect(() => {
    switch (state.loadingMessage) {
      case LOAD_MESSAGE.NONE:
        dispatch(hideLoadingBox())
        break
      case LOAD_MESSAGE.FETCHING_DATA:
        dispatch(showLoadingBox('Aguarde...', state.loadingMessage))
        break
      default:
        break
    }
  }, [dispatch, state.loadingMessage])

  useEffect(() => {
    if (state.errorMessage) {
      dispatch(showMessageBox(null, state.errorMessage))
      localDispatch.resetState()
    }
  }, [dispatch, localDispatch, state.errorMessage])

  useEffect(() => {
    setCollapsedByTp(prev => {
      const next = {}
      grupos.forEach(grupo => {
        next[grupo.tp_id] = prev[grupo.tp_id] || false
      })
      return next
    })
  }, [grupos])

  const toggleGrupo = tpId => {
    setCollapsedByTp(prev => ({
      ...prev,
      [tpId]: !prev[tpId],
    }))
  }

  return (
    <>
      <PageHeader>1.9 - Painel de Monitoramento de Entregas</PageHeader>
      <div style={{ marginTop: 16, minHeight: '80vh' }}>
        <Panel header={<TextBtn onClick={history.goBack}>Voltar</TextBtn>}>
          <div className="monitoramento-entregas__filters">
            <div className="monitoramento-entregas__periodo">
              <div className="monitoramento-entregas__periodo-label">Data Prevista da Rota</div>
              <Periodo
                periodoInicial={state.pInicial}
                periodoFinal={state.pFinal}
                showButton={false}
                onPesquisar={() => localDispatch.onBuscar()}
                onChangePeriodoInicial={localDispatch.onPeriodoInicialChange}
                onChangePeriodoFinal={localDispatch.onPeriodoFinalChange}
              />
            </div>
            <div className="monitoramento-entregas__analista">
              <CustomDropdown
                showLabel
                filter
                label="Analista"
                optionLabel="analista_name"
                options={state.analistas}
                value={state.selectedAnalista}
                disabled={!state.canSelectAnalista}
                onChange={localDispatch.onAnalistaChange}
                placeholder="Selecione..."
              />
            </div>
            <div className="monitoramento-entregas__buscar">
              <Btn onClick={() => localDispatch.onBuscar()}>Buscar</Btn>
            </div>
            {hasPermissaoDelegarRotas && state.selectedAnalista && (
              <>
                <div className="monitoramento-entregas__buscar">
                  <Btn onClick={localDispatch.openDelegarModal}>Delegar Rotas/Veículos</Btn>
                </div>
                <div className="monitoramento-entregas__buscar">
                  <Btn onClick={localDispatch.openAssumirModal}>Assumir Rotas/Veículos</Btn>
                </div>
              </>
            )}
          </div>

          {state.dados && grupos.length === 0 && (
            <div className="monitoramento-entregas__sem-rotas">
              Não há rotas vinculadas ao analista no período selecionado em nenhum TP associado ao
              usuário
            </div>
          )}

          {grupos.length > 0 && (
            <div className="monitoramento-entregas__grupos">
              {grupos.map(grupo => (
                <GrupoTp
                  key={grupo.tp_id}
                  grupo={grupo}
                  collapsed={collapsedByTp[grupo.tp_id]}
                  unreadChatFleets={unreadChatFleets}
                  onToggle={() => toggleGrupo(grupo.tp_id)}
                  onDeliveryClick={localDispatch.openEntregaModal}
                  onRotaInfoClick={localDispatch.openRotaModal}
                  onRetornoClick={localDispatch.openRetornoModal}
                />
              ))}
            </div>
          )}
        </Panel>
      </div>

      <ModalInformacoesEntrega
        visible={!!state.modalEntrega}
        delivery={state.modalEntrega}
        onClose={localDispatch.closeEntregaModal}
        onRefresh={localDispatch.onBuscar}
        onOcorrenciasVistas={localDispatch.markOcorrenciasEntregaVistas}
      />

      <ModalInformacoesRota
        visible={!!state.modalRota}
        rota={state.modalRota}
        onClose={localDispatch.closeRotaModal}
        onAnalistaTransferido={localDispatch.onBuscar}
      />

      <ModalDelegarRotas
        visible={state.modalDelegar}
        analistaOrigem={state.selectedAnalista}
        onClose={localDispatch.closeDelegarModal}
        onSuccess={localDispatch.onBuscar}
      />

      <ModalAssumirRotas
        visible={state.modalAssumir}
        analistaOrigem={state.selectedAnalista}
        onClose={localDispatch.closeAssumirModal}
        onSuccess={localDispatch.onBuscar}
      />

      <CustomModal
        header="Retorno do Veículo"
        isVisible={!!state.modalRetorno}
        onClose={localDispatch.closeRetornoModal}
        width="420px"
      >
        {state.modalRetorno && (
          <div className="monitoramento-entregas__retorno-modal">
            <InfoField label="Data" value={state.modalRetorno.returned_date || '-'} />
            <InfoField label="Hora" value={state.modalRetorno.returned_hour || '-'} />
          </div>
        )}
      </CustomModal>
    </>
  )
}

function GrupoTp({
  grupo,
  collapsed,
  unreadChatFleets,
  onToggle,
  onDeliveryClick,
  onRotaInfoClick,
  onRetornoClick,
}) {
  return (
    <section className="monitoramento-entregas__grupo">
      <button className="monitoramento-entregas__grupo-header" type="button" onClick={onToggle}>
        <div className="monitoramento-entregas__grupo-title">
          <span className="monitoramento-entregas__collapse-icon">
            {collapsed ? <FaChevronRight /> : <FaChevronDown />}
          </span>
          <strong>{grupo.tp_name}</strong>
        </div>
        <div className="monitoramento-entregas__grupo-status">
          {grupo.status_summary.map(status => (
            <StatusCircle
              key={status.status_id}
              statusId={status.status_id}
              label={status.label}
              value={status.total}
              isSummary
            />
          ))}
        </div>
        <div className="monitoramento-entregas__grupo-percentual">
          {formatPercent(grupo.percentual_status_final)}
        </div>
      </button>

      {!collapsed && (
        <div className="monitoramento-entregas__rotas">
          {grupo.rotas.map(rota => (
            <RotaCard
              key={rota.fleet_id}
              rota={rota}
              hasUnreadChat={unreadChatFleets.includes(Number(rota.fleet_id))}
              onDeliveryClick={onDeliveryClick}
              onRotaInfoClick={onRotaInfoClick}
              onRetornoClick={onRetornoClick}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function RotaCard({ rota, hasUnreadChat, onDeliveryClick, onRotaInfoClick, onRetornoClick }) {
  const driverTitle = `${rota.driver_name || ''}${rota.driver_phone ? ` - ${rota.driver_phone}` : ''}`
  const fleetTitle = `Rota ${rota.fleet_id}`
  const paradas = useMemo(() => agruparEntregasPorCliente(rota.deliveries), [rota.deliveries])

  return (
    <div className="monitoramento-entregas__rota-card">
      <div className="monitoramento-entregas__rota-placa" title={fleetTitle}>
        {rota.vehicle_badge}
      </div>
      <img
        className="monitoramento-entregas__driver-icon"
        src={IconDriver}
        alt="Motorista"
        title={driverTitle}
      />
      <div className="monitoramento-entregas__paradas">
        {paradas.map(parada => (
          <ParadaRow
            key={parada.client_id}
            rota={rota}
            parada={parada}
            onDeliveryClick={onDeliveryClick}
          />
        ))}
      </div>
      <button
        type="button"
        className={
          rota.returned_time_raw
            ? 'monitoramento-entregas__flag-button'
            : 'monitoramento-entregas__flag-button monitoramento-entregas__flag-button--blocked'
        }
        onClick={() => onRetornoClick(rota)}
        title={rota.returned_time_raw ? 'Retorno do Veículo' : 'Veículo ainda não retornado'}
      >
        <img src={IconFinalFlag} alt="Retorno do Veículo" />
      </button>
      <button
        type="button"
        className="monitoramento-entregas__info-button"
        onClick={() => onRotaInfoClick(rota)}
        title={hasUnreadChat ? 'Informações da Rota — nova mensagem no chat' : 'Informações da Rota'}
      >
        <img src={IconInfo} alt="Informações da Rota" />
        {hasUnreadChat && <span className="monitoramento-entregas__chat-dot" />}
      </button>
    </div>
  )
}

function ParadaRow({ rota, parada, onDeliveryClick }) {
  const totalEntregas = parada.deliveries.length
  const title = `${parada.nomeFantasia}\n${parada.address}, ${parada.number} - ${parada.city}-${
    parada.uf
  }\n${totalEntregas} ${totalEntregas === 1 ? 'entrega' : 'entregas'}`
  const farolClassNames = ['monitoramento-entregas__farol']
  if (parada.has_pending_ocorrencia) farolClassNames.push('monitoramento-entregas__farol--blinking')

  return (
    <div className="monitoramento-entregas__parada">
      <div className="monitoramento-entregas__parada-cliente" title={title}>
        <span
          className={farolClassNames.join(' ')}
          style={{
            backgroundColor: parada.finalizada ? colors.verde_success : colors.gray,
            color: parada.finalizada ? '#ffffff' : '#1f1f1f',
          }}
        >
          {totalEntregas}
        </span>
        <span className="monitoramento-entregas__parada-nome">{parada.nomeFantasia}</span>
      </div>
      <DeliveryScroller
        rota={rota}
        deliveries={parada.deliveries}
        onDeliveryClick={onDeliveryClick}
      />
    </div>
  )
}

function DeliveryScroller({ rota, deliveries, onDeliveryClick }) {
  const scrollerRef = useRef(null)
  const showArrows = deliveries.length > 7

  const scroll = direction => {
    if (!scrollerRef.current) return
    scrollerRef.current.scrollBy({ left: direction * 36, behavior: 'smooth' })
  }

  return (
    <div className="monitoramento-entregas__deliveries">
      {showArrows && (
        <button
          type="button"
          className="monitoramento-entregas__scroll-button"
          onClick={() => scroll(-1)}
          title="Voltar entregas"
        >
          <FaChevronLeft />
        </button>
      )}
      <div className="monitoramento-entregas__deliveries-window" ref={scrollerRef}>
        {deliveries.map(delivery => (
          <StatusCircle
            key={delivery.delivery_step_vehicle_id}
            statusId={delivery.status_id}
            label={delivery.status_label || delivery.status}
            blinking={!!delivery.has_pending_ocorrencia}
            value={delivery.has_canhoto ? 'C' : ''}
            title={`Identificador: ${delivery.identifier || '-'}\n${
              delivery.status_label || delivery.status
            }`}
            onClick={() =>
              onDeliveryClick({
                ...delivery,
                returned_time_raw: rota.returned_time_raw,
                returned_date: rota.returned_date,
                returned_hour: rota.returned_hour,
                vehicle_badge: rota.vehicle_badge,
              })
            }
          />
        ))}
      </div>
      {showArrows && (
        <button
          type="button"
          className="monitoramento-entregas__scroll-button"
          onClick={() => scroll(1)}
          title="Avançar entregas"
        >
          <FaChevronRight />
        </button>
      )}
    </div>
  )
}

function StatusCircle({ statusId, label, value, title, onClick, isSummary = false, blinking = false }) {
  const circleStyle = {
    backgroundColor: STATUS_COLORS[statusId] || colors.gray,
    color: shouldUseDarkText(statusId) ? '#1f1f1f' : '#ffffff',
  }
  const classNames = ['monitoramento-entregas__farol']
  if (isSummary) classNames.push('monitoramento-entregas__farol--summary')
  if (blinking) classNames.push('monitoramento-entregas__farol--blinking')

  const content = (
    <span className={classNames.join(' ')} style={circleStyle} title={title || label}>
      {value}
    </span>
  )

  if (!onClick) return content

  return (
    <button
      type="button"
      className="monitoramento-entregas__farol-button"
      onClick={onClick}
      title={title || label}
    >
      {content}
    </button>
  )
}

function InfoField({ label, value }) {
  return (
    <div className="monitoramento-entregas__info-field">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

// Uma parada por cliente, na ordem em que o cliente aparece na rota
function agruparEntregasPorCliente(deliveries) {
  const paradasByClientId = new Map()

  deliveries.forEach(delivery => {
    const parada = paradasByClientId.get(delivery.client_id)
    if (parada) {
      parada.deliveries.push(delivery)
      return
    }

    paradasByClientId.set(delivery.client_id, {
      client_id: delivery.client_id,
      nomeFantasia: delivery.nomeFantasia,
      address: delivery.address,
      number: delivery.number,
      city: delivery.city,
      uf: delivery.uf,
      deliveries: [delivery],
    })
  })

  return Array.from(paradasByClientId.values()).map(parada => ({
    ...parada,
    finalizada: parada.deliveries.every(delivery =>
      STATUS_FINAIS.includes(Number(delivery.status_id)),
    ),
    has_pending_ocorrencia: parada.deliveries.some(delivery => delivery.has_pending_ocorrencia),
  }))
}

function shouldUseDarkText(statusId) {
  return [11, 12, 13].includes(Number(statusId))
}

function formatPercent(value) {
  const numeric = Number(value) || 0
  if (Number.isInteger(numeric)) return `${numeric}%`
  return `${numeric.toFixed(1)}%`
}

const mapStateToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapStateToProps)(withRouter(PainelMonitoramentoEntregas))
